import { API } from "../api";
const BASE_URL = "/cooperatives/communications/groups";
const BASE_URL_2 = "/cooperatives/communications/messages";
const BASE_URL_3 = "/cooperatives/communications";

const communicationExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getGroupsNames: builder.query({
      query: (page = 1) => `${BASE_URL}?guard=staff&page=${page}`,
      transformResponse: (responseData) => responseData.data,
      providesTags: (result = {}) => {
        if (!result?.communication_groups)
          return [{ type: "CommGroupMgt", id: "LIST" }];
        return [
          { type: "CommGroupMgt", id: "LIST" },
          ...result.communication_groups.map(({ id }) => ({
            type: "CommGroupMgt",
            id: id,
          })),
        ];
      },
    }),
    getGroupById: builder.query({
      query: (groupId) => `${BASE_URL}/${groupId}?guard=staff`,
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, err, arg) => [{ type: "CommGroupMgt", id: arg }],
    }),
    createGroup: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/add?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "CommGroupMgt", id: "LIST" }],
    }),
    updateGroupName: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/${body.id}/edit?guard=staff`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommGroupMgt", id: arg.id },
      ],
    }),
    getGroupMembers: builder.query({
      query: ({ page = 1, groupId }) =>
        `${BASE_URL}/members/${groupId}?guard=staff&page=${page}`,
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, arg) => {
        if (!result?.communication_group_members)
          return [
            { type: "CommGroupMgt", id: "LIST" },
            { type: "CommGroupMgt", id: "MemberList" },
          ];
        return [
          { type: "CommGroupMgt", id: "LIST" },
          ...result?.communication_group_members.map(({ group_id }) => ({
            type: "CommGroupMgt",
            id: group_id,
          })),
        ];
      },
    }),
    addMembersToGroup: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/members/add?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommGroupMgt", id: arg.group_id },
        { type: "CommGroupMgt", id: "MemberList" },
      ],
    }),
    removeMemberFromGroup: builder.mutation({
      query: (recordId) => ({
        url: `${BASE_URL}/members/${recordId}?guard=staff`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommGroupMgt", id: "MemberList" },
        { type: "CommGroupMgt", id: "LIST" },
      ],
    }),
    createMessage: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL_2}/${body.endpoint}?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [
        { type: "CommGroupMessage", id: "LIST" },
        "StaffDashboard",
        "CommunicationBalances",
      ],
    }),
    sendDraftedMessage: builder.mutation({
      query: (messageId) => ({
        url: `${BASE_URL_2}/${messageId}/sendDrafted?guard=staff`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CommGroupMessage", id: "LIST" },
        "StaffDashboard",
        "CommunicationBalances",
      ],
    }),

    updateDraftMessage: builder.mutation({
      query: ({ messageId, body }) => ({
        url: `${BASE_URL_2}/${messageId}/updateDraft?guard=staff`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommGroupMessage", id: "LIST" },
        { type: "CommGroupMessage", id: arg.messageId },
      ],
    }),
    getCommunicationMessages: builder.query({
      query: ({ page, status }) =>
        `${BASE_URL_2}?guard=staff&page=${page || 1}&status=${
          status || "DRAFTED"
        }&limit=20`,
      transformResponse: (responseData) => responseData.data,
      providesTags: () => {
        return [{ type: "CommGroupMessage", id: "LIST" }];
      },
    }),
    getCommunicationBalances: builder.query({
      query: () => `${BASE_URL_3}/balances?guard=staff`,
      transformResponse: (responseData) => responseData.data,
      providesTags: ["CommunicationBalances"],
    }),
    getMessageDetail: builder.query({
      query: (messageId) => `${BASE_URL_2}/${messageId}?guard=staff`,
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, err, arg) => [
        { type: "CommGroupMessage", id: arg },
        { type: "CommGroupMessage", id: "LIST" },
      ],
    }),
    composeMessage: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL_3}/compose?guard=staff`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData.data,
    }),
    initializeSmsTopup: builder.mutation({
      query: (body) => ({
        url: `/cooperatives/sms-topup-requests/initialize?guard=staff`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data ?? responseData,
    }),
    createSmsTopupAfterPayment: builder.mutation({
      query: (body) => ({
        url: `/cooperatives/sms-topup-requests/create?guard=staff`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data ?? responseData,
      invalidatesTags: ["StaffDashboard", "CommunicationBalances"],
    }),
  }),
});

export const {
  useCreateGroupMutation,
  useGetGroupsNamesQuery,
  useUpdateGroupNameMutation,
  useGetGroupMembersQuery,
  useGetGroupByIdQuery,
  useAddMembersToGroupMutation,
  useRemoveMemberFromGroupMutation,
  useCreateMessageMutation,
  useSendDraftedMessageMutation,
  useUpdateDraftMessageMutation,
  useGetCommunicationMessagesQuery,
  useGetCommunicationBalancesQuery,
  useGetMessageDetailQuery,
  useComposeMessageMutation,
  useInitializeSmsTopupMutation,
  useCreateSmsTopupAfterPaymentMutation,
} = communicationExtendedApi;
